import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/auth/Context";
import { useToast } from "../../contexts/toast/Context";
import {
  getGymGatewayConfigRequest,
  saveGymGatewayConfigRequest,
  deleteGymGatewayConfigRequest,
  getMercadoPagoAuthorizeUrlRequest,
  MP_CONNECT_GENERIC_ERROR,
} from "../requests/gym";
import { MANAGER_GATEWAY_CONFIG_QUERY_KEY } from "../../query/keys";

export const useManagerGatewayConfig = () => {
  const queryClient = useQueryClient();
  const { token } = useAuth();
  const { showToast } = useToast();

  const { data: gatewayConfig, isLoading } = useQuery({
    queryKey: MANAGER_GATEWAY_CONFIG_QUERY_KEY,
    queryFn: () => getGymGatewayConfigRequest(token!),
    enabled: !!token,
  });

  const { mutate: saveConfig, isPending: isSaving } = useMutation({
    mutationFn: (params: {
      provider: string;
      credentials: Record<string, string>;
    }) =>
      saveGymGatewayConfigRequest(params.provider, params.credentials, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: MANAGER_GATEWAY_CONFIG_QUERY_KEY,
      });
      showToast(`Proveedor configurado correctamente`, "success");
    },
    onError: (error: Error) => {
      showToast(
        error.message ?? "Error al configurar el proveedor de pagos",
        "error",
      );
    },
  });

  const { mutate: deleteConfig, isPending: isDeleting } = useMutation({
    mutationFn: (provider: string) =>
      deleteGymGatewayConfigRequest(provider, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: MANAGER_GATEWAY_CONFIG_QUERY_KEY,
      });
      showToast("Configuracion del proveedor eliminada", "success");
    },
    onError: (error: Error) => {
      showToast(error.message ?? "Error al eliminar la configuracion", "error");
    },
  });

  const { mutate: connectMercadoPago, isPending: isConnecting } = useMutation({
    mutationFn: () => getMercadoPagoAuthorizeUrlRequest(token!),
    onSuccess: ({ url }) => {
      if (!url) {
        showToast(MP_CONNECT_GENERIC_ERROR, "error");
        return;
      }
      window.location.assign(url);
    },
    onError: (error: Error) => {
      showToast(error.message || MP_CONNECT_GENERIC_ERROR, "error");
    },
  });

  return {
    gatewayConfig,
    isLoading,
    saveConfig,
    isSaving,
    deleteConfig,
    isDeleting,
    connectMercadoPago,
    isConnecting,
  };
};
